import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import AdBanner from '../components/AdBanner';
import { usePageSEO } from '../components/SEO';

const Ascoltaci = () => {
  const { t, language } = useTranslation();
  usePageSEO({
    it: {
      title: 'Ascoltaci — Radio Live, Video e DAB+ | Rete Italia',
      description: 'Ascolta Rete Italia in diretta: streaming audio e video live e DAB+ a Melbourne, Sydney, Brisbane e Perth, più le frequenze FM regionali.',
    },
    en: {
      title: 'Listen — Live Radio, Video & DAB+ | Rete Italia',
      description: 'Listen to Rete Italia live: live audio and video streaming and DAB+ in Melbourne, Sydney, Brisbane and Perth, plus regional FM frequencies.',
    },
    basePath: '/ascoltaci',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 pt-24 pb-16">
      <div className="container mx-auto px-4">
        {/* Advertisement Banner */}
        <AdBanner className="mb-8" />

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-gradient-to-r from-[#009246] via-[#CE2B37] to-[#00247D] bg-clip-text text-transparent">
              {language === 'it' ? 'Ascoltaci' : 'Listen To Us'}
            </span>
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            {language === 'it' 
              ? 'Scopri tutti i modi per ascoltare Rete Italia in Australia' 
              : 'Discover all the ways to listen to Rete Italia in Australia'}
          </p>
        </div>

        {/* Main Frequencies Box */}
        <div className="bg-gradient-to-r from-[#00247D] to-[#0047AB] rounded-3xl p-8 md:p-12 text-white shadow-2xl mb-12">
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 flex items-center justify-center gap-3">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
              </svg>
              {language === 'it' ? 'Ascoltaci Su' : 'Listen To Us On'}
            </h2>
            
            {/* DAB+ Cities */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 opacity-90">
                {language === 'it' ? 'Radio Digitale' : 'Digital Radio'}
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                <span className="bg-white/20 px-4 py-2 rounded-lg font-semibold">Sydney <span className="text-yellow-300">DAB+</span></span>
                <span className="bg-white/20 px-4 py-2 rounded-lg font-semibold">Melbourne <span className="text-yellow-300">DAB+</span></span>
                <span className="bg-white/20 px-4 py-2 rounded-lg font-semibold">Brisbane <span className="text-yellow-300">DAB+</span></span>
                <span className="bg-white/20 px-4 py-2 rounded-lg font-semibold">Perth (Niche Radio) <span className="text-yellow-300">DAB+</span></span>
              </div>
            </div>

            {/* FM Frequencies */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold mb-4 opacity-90">
                {language === 'it' ? 'Frequenze FM' : 'FM Frequencies'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="bg-white/10 p-4 rounded-xl">
                  <p className="font-bold text-lg">Colac</p>
                  <p className="text-2xl font-bold text-yellow-300">87.8 FM</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl">
                  <p className="font-bold text-lg">Shepparton</p>
                  <p className="text-2xl font-bold text-yellow-300">87.6 FM</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl">
                  <p className="font-bold text-lg">Wangaratta</p>
                  <p className="text-2xl font-bold text-yellow-300">87.8 FM</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl">
                  <p className="font-bold text-lg">Wodonga</p>
                  <p className="text-2xl font-bold text-yellow-300">87.6 FM</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl">
                  <p className="font-bold text-lg">Albury</p>
                  <p className="text-2xl font-bold text-yellow-300">87.6 FM</p>
                </div>
                <div className="bg-white/10 p-4 rounded-xl">
                  <p className="font-bold text-lg">Swan Hill</p>
                  <p className="text-2xl font-bold text-yellow-300">87.6 FM</p>
                </div>
              </div>
            </div>

            {/* App Download Links */}
            <div className="pt-6 border-t border-white/20">
              <h3 className="text-xl font-semibold mb-4">
                {language === 'it' ? 'Scarica la nostra App' : 'Download our App'}
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                <a 
                  href="https://play.google.com/store/apps/details?id=com.xdevel.reteitaliaaustralia2&hl=en_AU" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-black hover:bg-gray-900 px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                  </svg>
                  <span className="font-semibold">Google Play</span>
                </a>
                <a 
                  href="https://apps.apple.com/au/app/rete-italia/id1445988538" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-black hover:bg-gray-900 px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="font-semibold">App Store</span>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Online Streaming Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#009246] to-[#00b359] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">{language === 'it' ? 'Streaming Audio' : 'Audio Streaming'}</h3>
            <p className="text-gray-600 mb-4">
              {language === 'it' 
                ? 'Ascolta la radio in diretta dal nostro sito web' 
                : 'Listen to live radio from our website'}
            </p>
            <a href="/" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#009246] to-[#00b359] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300">
              {language === 'it' ? 'Ascolta Ora' : 'Listen Now'}
            </a>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#CE2B37] to-[#e63946] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">{language === 'it' ? 'Streaming Video' : 'Video Streaming'}</h3>
            <p className="text-gray-600 mb-4">
              {language === 'it' 
                ? 'Guarda Rete Italia TV in diretta' 
                : 'Watch Rete Italia TV live'}
            </p>
            <a href="/" className="inline-flex items-center gap-2 bg-gradient-to-r from-[#CE2B37] to-[#e63946] text-white px-6 py-3 rounded-xl font-semibold hover:scale-105 transition-all duration-300">
              {language === 'it' ? 'Guarda Ora' : 'Watch Now'}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Ascoltaci;
